import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import chatService from 'services/chatService';
import documentService from 'services/documentService';
import verificationService from 'services/verificationService';
import conversationService from 'services/conversationService';
import feedbackService from 'services/feedbackService';
import reclassificationService from 'services/reclassificationService';
import reasoningService, { ReasoningEvent } from 'services/reasoningService';
import { analyticsService } from 'services/analyticsService';
import useAuthStore from 'stores/authStore';
import {
  Message,
  MessageType,
  ProcessingStatus,
  ChatStore,
  ReasoningStep,
} from 'types/chat.types';

export { MessageType, ProcessingStatus };

const useChatStore = create<ChatStore>()(
  devtools(
    (set, get) => ({
      // Existing state
      messages: [],
      currentJobId: null,
      currentConversationId: null,
      processingStatus: ProcessingStatus.IDLE,
      uploadProgress: 0,
      error: null,

      // Verification state
      verificationSessionId: null,
      verificationStatus: null,
      currentQuestion: null,
      verifiedData: null,
      isVerifying: false,

      // Feedback state
      feedbackMap: new Map(),

      // Reclassification state
      pendingReclassification: null,

      // Classification options
      skipTariff: true,
      setSkipTariff: (value: boolean) => set({ skipTariff: value }),

      addMessage: (message: Partial<Message>) => {
        const newMessage: Message = {
          id: Date.now(),
          timestamp: new Date(),
          ...message,
        } as Message;
        
        set((state) => ({
          messages: [...state.messages, newMessage],
        }));
      },

      updateLastMessage: (updates: Partial<Message>) => {
        set((state) => {
          const messages = [...state.messages];
          if (messages.length > 0) {
            messages[messages.length - 1] = {
              ...messages[messages.length - 1],
              ...updates,
            } as Message;
          }
          return { messages };
        });
      },

      clearMessages: () => {
        set({ messages: [] });
      },

      setProcessingStatus: (status: ProcessingStatus) => {
        set({ processingStatus: status });
      },

      setUploadProgress: (progress: number) => {
        set({ uploadProgress: progress });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      sendMessage: async (message: string, file?: File, options?: { skipTariff?: boolean }, additionalFiles?: File[]) => {
        const { addMessage, setProcessingStatus, setUploadProgress, setError, currentConversationId, pendingReclassification, skipTariff: storeSkipTariff } = get();
        const resolvedOptions = { skipTariff: options?.skipTariff ?? storeSkipTariff, ...options };

        console.log('📤 [chatStore] sendMessage called', { message, hasFile: !!file, conversationId: currentConversationId, hasPendingReclassification: !!pendingReclassification, options: resolvedOptions });

        // Check if we're handling a reclassification request
        if (pendingReclassification && !file) {
          console.log('🔄 [chatStore] Handling pending reclassification');
          const { documentId } = pendingReclassification;

          // Clear pending reclassification state
          set({ pendingReclassification: null });

          // Submit the reclassification
          await get().requestReclassification(documentId, message, file);
          return;
        }

        try {
          setError(null);

          // Add user message to chat
          if (file) {
            console.log('📎 [chatStore] Adding USER_FILE message');
            addMessage({
              type: MessageType.USER_FILE,
              fileName: file.name,
              fileSize: file.size,
              content: message || `Uploaded: ${file.name}`,
            });
          } else {
            console.log('[chatStore] Adding USER_TEXT message');
            addMessage({
              type: MessageType.USER_TEXT,
              content: message,
            });
          }

          // Set processing status if file is being uploaded
          if (file) {
            console.log('⬆️ [chatStore] Setting UPLOADING status');
            setProcessingStatus(ProcessingStatus.UPLOADING);
          } else {
            // Add thinking indicator for text-only queries
            console.log('💭 [chatStore] Adding THINKING indicator');
            addMessage({
              type: MessageType.THINKING,
              content: 'Thinking...',
            });
          }

          console.log('🌐 [chatStore] Calling chatService.sendMessage...');

          // Track message sent
          analyticsService.trackMessageSent({
            messageType: file ? (message ? 'both' : 'file') : 'text',
            conversationId: currentConversationId || undefined,
            hasAttachment: !!file,
          });

          // Track classification started if file is present
          if (file) {
            analyticsService.trackClassificationStarted({
              type: message ? 'both' : 'file',
              hasFile: true,
              fileType: file.type,
              fileSize: file.size,
              conversationId: currentConversationId || undefined,
            });

            analyticsService.trackFileUploadStarted({
              fileType: file.type,
              fileSize: file.size,
              fileName: file.name,
            });
          }

          const classificationStartTime = Date.now();

          // Send message via chat service
          // IMPORTANT: Agent decides response type based on content, not file presence
          // - Text queries can trigger 'immediate' OR 'job' responses
          // - File uploads always trigger 'job' responses
          const response = await chatService.sendMessage(
            message,
            file,
            file ? (progress: number) => {
              setUploadProgress(progress);
            } : null,
            currentConversationId,
            resolvedOptions,
            additionalFiles
          );

          console.log('✅ [chatStore] Received response:', { type: response.type, hasJobId: !!(response as any).job_id, conversationId: response.conversation_id });

          // Store conversation_id from response
          if (response.conversation_id) {
            console.log('[chatStore] Storing conversation_id:', response.conversation_id);
            set({ currentConversationId: response.conversation_id });
          }

          if (response.type === 'immediate') {
            console.log('⚡ [chatStore] Processing IMMEDIATE response');
            console.log('[chatStore] Removing THINKING indicator');
            // Remove thinking indicator and add immediate text response
            set((state) => ({
              messages: state.messages.filter(msg => msg.type !== MessageType.THINKING)
            }));

            console.log('🤖 [chatStore] Adding ASSISTANT message');
            addMessage({
              type: MessageType.ASSISTANT,
              content: response.answer,
            });

            console.log('✅ [chatStore] IMMEDIATE response complete');
            setUploadProgress(0);
            setProcessingStatus(ProcessingStatus.IDLE);
          } else {
            console.log('⏳ [chatStore] Processing JOB response');
            // Job-based response (file upload or text classification)
            // Remove thinking indicator if present
            console.log('[chatStore] Removing THINKING indicator');
            set((state) => ({
              messages: state.messages.filter(msg => msg.type !== MessageType.THINKING)
            }));

            console.log('💼 [chatStore] Setting job_id:', response.job_id);
            set({ currentJobId: response.job_id });

            // Log if text classification was triggered
            if (response.metadata?.classification_triggered) {
              console.log(' [chatStore] Text classification triggered for text-only query');
            }

            // Use answer field (new) or fallback to message field (deprecated)
            const displayContent = response.answer || response.message || 'Processing your request...';
            console.log('📝 [chatStore] Display content:', displayContent);

            console.log('⚙️ [chatStore] Adding PROCESSING message');
            addMessage({
              type: MessageType.PROCESSING,
              content: displayContent,
              jobId: response.job_id,
              status: response.tracking.status,
            });

            console.log('[chatStore] Setting QUEUED status');
            setProcessingStatus(ProcessingStatus.QUEUED);

            console.log('[chatStore] Starting SSE job watch for:', response.job_id);

            // Start the reasoning SSE stream in parallel (fire-and-forget).
            // This streams the agent's chain-of-thought into the chat.
            reasoningService.watchReasoning(response.job_id, (event: ReasoningEvent) => {
              console.log('[chatStore] Reasoning event:', event.type, event.phase, event.iteration);

              if (event.type === 'ask_user') {
                get().addMessage({
                  type: MessageType.AGENT_QUESTION,
                  content: 'Agent needs clarification',
                  jobId: response.job_id,
                  questions: event.data.questions || [],
                });
                return;
              }

              if (event.type === 'stream_end' || event.type === 'classification_complete' || event.type === 'error') {
                const msgs = get().messages;
                const last = msgs[msgs.length - 1];
                if (last?.type === MessageType.REASONING) {
                  get().updateLastMessage({ isComplete: true } as Partial<Message>);
                }
                return;
              }

              // Re-read current messages on every event (avoid stale closure)
              const currentMessages = get().messages;
              const lastMsg = currentMessages[currentMessages.length - 1];
              const step: ReasoningStep = {
                type: event.type as ReasoningStep['type'],
                iteration: event.iteration,
                phase: event.phase,
                data: event.data,
              };

              if (lastMsg?.type === MessageType.REASONING) {
                get().updateLastMessage({
                  steps: [...(lastMsg as any).steps, step],
                  currentPhase: event.phase || (lastMsg as any).currentPhase,
                } as Partial<Message>);
              } else if (lastMsg?.type === MessageType.PROCESSING) {
                get().updateLastMessage({
                  type: MessageType.REASONING,
                  steps: [step],
                  currentPhase: event.phase || 'chapter_identification',
                } as any);
              } else {
                // A RESULT message may have been inserted after the REASONING
                // message (via onPartialResult). Find the existing REASONING
                // message and update it in-place instead of appending a duplicate.
                const existingIdx = currentMessages.findIndex(
                  (m) => m.type === MessageType.REASONING
                );
                if (existingIdx !== -1) {
                  const existing = currentMessages[existingIdx] as any;
                  set((state) => {
                    const updated = [...state.messages];
                    updated[existingIdx] = {
                      ...updated[existingIdx],
                      steps: [...(existing.steps || []), step],
                      currentPhase: event.phase || existing.currentPhase,
                    } as Message;
                    return { messages: updated };
                  });
                } else if (!currentMessages.some((m) => m.type === MessageType.RESULT)) {
                  get().addMessage({
                    type: MessageType.REASONING,
                    content: 'Classifying...',
                    steps: [step],
                    currentPhase: event.phase || 'chapter_identification',
                  });
                }
              }
            }).catch((err) => { console.warn('[chatStore] Reasoning SSE error:', err); });

            const result = await documentService.watchJobSSE(
              response.job_id,
              {
                onProgress: (status, progress, _message, meta) => {
                  console.log('📡 [chatStore] SSE progress:', status, progress, _message, meta);

                  // Only update the PROCESSING message while it's still the last one
                  const lastMsg = get().messages[get().messages.length - 1];
                  if (lastMsg?.type === MessageType.PROCESSING) {
                    const updates: Partial<Message> = {
                      status,
                      progress,
                      content: _message,
                    };
                    if (meta?.file_extraction_status) {
                      updates.fileExtractionStatus = meta.file_extraction_status as any;
                    }
                    if (meta?.pipeline_stage) {
                      updates.pipelineStage = meta.pipeline_stage;
                    }
                    get().updateLastMessage(updates);
                  }

                  if (status === 'verification_in_progress') {
                    console.log('🔐 [chatStore] Verification in progress');
                    setProcessingStatus(ProcessingStatus.VERIFICATION_IN_PROGRESS);
                    documentService.getStatus(response.job_id).then((snap) => {
                      if (snap.verification_session_id) {
                        get().setVerificationSession(snap.verification_session_id);
                        get().loadVerificationQuestion();
                      }
                    }).catch(() => {});
                    return;
                  }

                  if (status === 'processing') {
                    setProcessingStatus(ProcessingStatus.PROCESSING);
                    analyticsService.trackClassificationInProgress({
                      jobId: response.job_id,
                      conversationId: response.conversation_id,
                      progress,
                      status,
                    });
                  }

                  if (status === 'finalizing') {
                    console.log('🏁 [chatStore] Status: FINALIZING');
                    setProcessingStatus(ProcessingStatus.FINALIZING);
                  }
                },

                onPartialResult: (htsClassification) => {
                  // Guard: skip if any RESULT card already exists (loading or complete).
                  // Prevents the second SSE connection (resumePolling) from adding a
                  // duplicate card after the first path has already patched the result.
                  const alreadyShown = get().messages.some(
                    (m) => m.type === MessageType.RESULT
                  );
                  if (alreadyShown) return;

                  console.log('⚡ [chatStore] Early HTS result received — showing immediately');
                  // Collapse the PROCESSING message (progress 100 → renders null)
                  const msgs = get().messages;
                  const processingIdx = [...msgs].reverse().findIndex(m => m.type === MessageType.PROCESSING);
                  if (processingIdx !== -1) {
                    const actualIdx = msgs.length - 1 - processingIdx;
                    set((state) => {
                      const updated = [...state.messages];
                      updated[actualIdx] = { ...updated[actualIdx], progress: 100 } as Message;
                      return { messages: updated };
                    });
                  }
                  // Show HTS card with a tariff loading skeleton beneath it
                  addMessage({
                    type: MessageType.RESULT,
                    content: 'Classification complete',
                    result: {
                      hts_classification: htsClassification,
                      tariff_loading: true,
                    },
                  });
                },
              }
            );

            console.log('✅ [chatStore] SSE complete, full result received');
            reasoningService.disconnect();

            // Mark reasoning message as complete so the header transitions.
            // The REASONING message may not be the last one (onPartialResult
            // can add a RESULT message before watchJobSSE resolves).
            set((state) => {
              const updated = [...state.messages];
              const reasoningIdx = updated.findIndex((m) => m.type === MessageType.REASONING);
              if (reasoningIdx !== -1) {
                updated[reasoningIdx] = { ...updated[reasoningIdx], isComplete: true } as Message;
              }
              return { messages: updated };
            });

            setProcessingStatus(ProcessingStatus.COMPLETED);
            get()._applyFinalResult(result);

            // Track classification completed
            const duration = Date.now() - classificationStartTime;
            analyticsService.trackClassificationCompleted({
              jobId: response.job_id,
              conversationId: response.conversation_id,
              duration,
              hasHTSCode: !!(result?.hts_code || result?.classification),
              confidence: result?.confidence,
            });

            if (file) {
              analyticsService.trackFileUploadCompleted({
                fileType: file.type,
                fileSize: file.size,
                duration,
              });
            }

            console.log('✅ [chatStore] JOB response complete');
            setUploadProgress(0);
            setProcessingStatus(ProcessingStatus.IDLE);
          }
        } catch (error: any) {
          console.error('❌ [chatStore] Send message error:', error);
          reasoningService.disconnect();

          // Track classification error
          analyticsService.trackClassificationError({
            error: error.message,
            jobId: get().currentJobId || undefined,
            conversationId: currentConversationId || undefined,
          });

          if (file) {
            analyticsService.trackFileUploadFailed({
              fileType: file.type,
              fileSize: file.size,
              error: error.message,
            });
          }

          // Remove thinking indicator if present
          console.log('[chatStore] Removing THINKING indicator (error cleanup)');
          set((state) => ({
            messages: state.messages.filter(msg => msg.type !== MessageType.THINKING)
          }));

          console.log('⚠️ [chatStore] Setting FAILED status');
          setProcessingStatus(ProcessingStatus.FAILED);
          setError(error.message);

          console.log('❌ [chatStore] Adding ERROR message');
          addMessage({
            type: MessageType.ERROR,
            content: `Error: ${error.message}`,
            error: error.message,
          });

          setUploadProgress(0);
          setProcessingStatus(ProcessingStatus.IDLE);
        }
      },

      uploadDocument: async (file: File, message?: string) => {
        const { addMessage, setProcessingStatus, setUploadProgress, setError } = get();

        try {
          setError(null);

          addMessage({
            type: MessageType.USER_FILE,
            fileName: file.name,
            fileSize: file.size,
            content: message || `Uploaded: ${file.name}`,
          });

          setProcessingStatus(ProcessingStatus.UPLOADING);

          const uploadResponse = await documentService.uploadAsync(file, message, (progress: number) => {
            setUploadProgress(progress);
          }) as any;

          set({ currentJobId: uploadResponse.job_id });

          addMessage({
            type: MessageType.PROCESSING,
            content: 'Processing your document...',
            jobId: uploadResponse.job_id,
            status: uploadResponse.status,
            queuePosition: uploadResponse.queue_position,
          });

          setProcessingStatus(ProcessingStatus.QUEUED);

          // Start reasoning SSE in parallel for file uploads too
          reasoningService.watchReasoning(uploadResponse.job_id, (event: ReasoningEvent) => {
            const { addMessage: addMsg } = get();

            if (event.type === 'ask_user') {
              addMsg({
                type: MessageType.AGENT_QUESTION,
                content: 'Agent needs clarification',
                jobId: uploadResponse.job_id,
                questions: event.data.questions || [],
              });
              return;
            }

            if (event.type === 'stream_end' || event.type === 'classification_complete' || event.type === 'error') {
              const currentMsgs = get().messages;
              const last = currentMsgs[currentMsgs.length - 1];
              if (last?.type === MessageType.REASONING) {
                get().updateLastMessage({ isComplete: true } as Partial<Message>);
              }
              return;
            }

            const currentMessages = get().messages;
            const lastMsg = currentMessages[currentMessages.length - 1];
            const step: ReasoningStep = {
              type: event.type as ReasoningStep['type'],
              iteration: event.iteration,
              phase: event.phase,
              data: event.data,
            };

            if (lastMsg?.type === MessageType.REASONING) {
              get().updateLastMessage({
                steps: [...(lastMsg as any).steps, step],
                currentPhase: event.phase || (lastMsg as any).currentPhase,
              } as Partial<Message>);
            } else if (lastMsg?.type === MessageType.PROCESSING) {
              get().updateLastMessage({
                type: MessageType.REASONING,
                steps: [step],
                currentPhase: event.phase || 'chapter_identification',
              } as any);
            } else {
              const existingIdx = currentMessages.findIndex(
                (m) => m.type === MessageType.REASONING
              );
              if (existingIdx !== -1) {
                const existing = currentMessages[existingIdx] as any;
                set((state) => {
                  const updated = [...state.messages];
                  updated[existingIdx] = {
                    ...updated[existingIdx],
                    steps: [...(existing.steps || []), step],
                    currentPhase: event.phase || existing.currentPhase,
                  } as Message;
                  return { messages: updated };
                });
              } else if (!currentMessages.some((m) => m.type === MessageType.RESULT)) {
                addMsg({
                  type: MessageType.REASONING,
                  content: 'Classifying...',
                  steps: [step],
                  currentPhase: event.phase || 'chapter_identification',
                });
              }
            }
          }).catch(() => {});

          const result = await documentService.pollJobStatus(
            uploadResponse.job_id,
            (statusUpdate: any) => {
              // Only update processing message if it's still the last one
              const lastMsg = get().messages[get().messages.length - 1];
              if (lastMsg?.type === MessageType.PROCESSING) {
                get().updateLastMessage({
                  status: statusUpdate.status,
                  progress: statusUpdate.progress,
                } as Partial<Message>);
              }

              // Handle verification status
              if (statusUpdate.status === 'verification_in_progress') {
                setProcessingStatus(ProcessingStatus.VERIFICATION_IN_PROGRESS);

                if (statusUpdate.verification_session_id) {
                  get().setVerificationSession(statusUpdate.verification_session_id);
                  get().loadVerificationQuestion();
                }

                // Stop here - verification flow takes over
                return;
              }

              if (statusUpdate.status === 'processing') {
                setProcessingStatus(ProcessingStatus.PROCESSING);
              }

              if (statusUpdate.status === 'finalizing') {
                setProcessingStatus(ProcessingStatus.FINALIZING);
              }
            }
          );

          // Only reached if no verification required
          reasoningService.disconnect();
          setProcessingStatus(ProcessingStatus.COMPLETED);

          addMessage({
            type: MessageType.RESULT,
            content: 'Processing completed',
            result: result,
          });

          setUploadProgress(0);
          setProcessingStatus(ProcessingStatus.IDLE);
        } catch (error: any) {
          console.error('Upload/Processing error:', error);
          
          setProcessingStatus(ProcessingStatus.FAILED);
          setError(error.message);

          addMessage({
            type: MessageType.ERROR,
            content: `Error: ${error.message}`,
            error: error.message,
          });

          setUploadProgress(0);
          setProcessingStatus(ProcessingStatus.IDLE);
        }
      },

      cancelJob: async () => {
        const { currentJobId, setProcessingStatus, addMessage } = get();

        if (!currentJobId) return;

        try {
          await documentService.cancelJob(currentJobId);
          
          setProcessingStatus(ProcessingStatus.CANCELLED);
          
          addMessage({
            type: MessageType.SYSTEM,
            content: 'Processing cancelled',
          });

          setProcessingStatus(ProcessingStatus.IDLE);
        } catch (error: any) {
          console.error('Cancel job error:', error);
          get().setError(error.message);
          setProcessingStatus(ProcessingStatus.IDLE);
        }
      },

      // Verification actions
      setVerificationSession: (sessionId: string) => {
        // Track verification started
        analyticsService.trackVerificationStarted(sessionId);

        set({
          verificationSessionId: sessionId,
          isVerifying: true,
        });
      },

      loadVerificationQuestion: async () => {
        const { verificationSessionId, addMessage, setError } = get();

        if (!verificationSessionId) {
          setError('No verification session found');
          return;
        }

        try {
          const response = await verificationService.getStatus(verificationSessionId);

          set({
            verificationStatus: response.status,
            currentQuestion: response.current_question,
          });

          // Add verification question message
          addMessage({
            type: MessageType.VERIFICATION_QUESTION,
            sessionId: verificationSessionId,
            question: response.current_question,
          });
        } catch (error: any) {
          console.error('Load verification question error:', error);
          setError(error.message);
          set({ isVerifying: false });
        }
      },

      submitVerificationAnswer: async (additionalInfo?: string) => {
        const { verificationSessionId, addMessage, setError, setProcessingStatus } = get();

        if (!verificationSessionId) {
          setError('No verification session found');
          return;
        }

        try {
          let response: any;

          if (additionalInfo) {
            // User is providing additional information
            addMessage({
              type: MessageType.VERIFICATION_RESPONSE,
              sessionId: verificationSessionId,
              userResponse: additionalInfo,
            });

            response = await verificationService.provideAdditionalInformation(
              verificationSessionId,
              additionalInfo
            );
          } else {
            // User wants to continue with available data
            addMessage({
              type: MessageType.SYSTEM,
              content: 'Continuing with available data...',
            });

            response = await verificationService.continueWithAvailableData(
              verificationSessionId
            );
          }

          set({
            verificationStatus: response.status,
          });

          // Check response status
          if (response.status === 'awaiting_comprehensive_response') {
            // Data was updated, show updated question for review
            set({ currentQuestion: response.question || null });

            addMessage({
              type: MessageType.VERIFICATION_QUESTION,
              sessionId: verificationSessionId,
              question: response.question,
            });
          } else if (response.status === 'completed') {
            // Verification complete, continue with HTS classification
            // Track verification completed
            analyticsService.trackVerificationCompleted(
              verificationSessionId,
              additionalInfo ? 'additional_info' : 'continue_with_available'
            );

            set({
              isVerifying: false,
              verifiedData: response.verified_data || null,
            });

            setProcessingStatus(ProcessingStatus.FINALIZING);

            addMessage({
              type: MessageType.SYSTEM,
              content: 'Verification completed. Running HTS classification...',
            });

            // Resume polling for final result
            get().resumePolling();
          }
        } catch (error: any) {
          console.error('Submit verification answer error:', error);
          setError(error.message);

          addMessage({
            type: MessageType.ERROR,
            content: `Verification error: ${error.message}`,
            error: error.message,
          });
        }
      },

      clearVerification: () => {
        set({
          verificationSessionId: null,
          verificationStatus: null,
          currentQuestion: null,
          verifiedData: null,
          isVerifying: false,
        });
      },

      // Internal helper: idempotent result finalizer.
      // Patches the early tariff-loading card in-place, or adds a new RESULT message.
      // Guards against duplicate calls (e.g. SSE + resumePolling both resolving).
      _applyFinalResult: (result: any) => {
        const { addMessage, currentConversationId } = get();

        // If a completed (non-loading) RESULT message already exists, skip.
        const alreadyDone = get().messages.some(
          (m) => m.type === MessageType.RESULT && !(m as any).result?.tariff_loading
        );
        if (alreadyDone) {
          console.log('[chatStore] Result already applied, skipping duplicate');
          return;
        }

        const msgs = get().messages;
        const earlyIdx = msgs.findIndex(
          (m) => m.type === MessageType.RESULT && (m as any).result?.tariff_loading === true
        );
        if (earlyIdx !== -1) {
          console.log('[chatStore] Patching early result card with full tariff data');
          set((state) => {
            const updated = [...state.messages];
            updated[earlyIdx] = { ...updated[earlyIdx], result } as Message;
            return { messages: updated };
          });
        } else {
          console.log('[chatStore] Adding RESULT message');
          addMessage({ type: MessageType.RESULT, content: 'Processing completed', result });
        }

        // Persist the reasoning + classification result to the conversation
        // so both appear when reloading from "Recent Conversations".
        if (currentConversationId) {
          const userId = useAuthStore.getState().getUserId();
          if (userId) {
            const nowMs = Date.now();
            const messagesToSave: any[] = [];

            // Include reasoning message if it exists
            const reasoningMsg = get().messages.find(
              (m) => m.type === MessageType.REASONING
            ) as any;
            if (reasoningMsg) {
              messagesToSave.push({
                id: nowMs,
                type: MessageType.REASONING,
                timestamp: reasoningMsg.timestamp?.toISOString?.() || new Date().toISOString(),
                content: reasoningMsg.content || 'Classification reasoning',
                steps: reasoningMsg.steps || [],
                currentPhase: reasoningMsg.currentPhase || 'complete',
                isComplete: true,
              });
            }

            messagesToSave.push({
              id: nowMs + 1,
              type: 'classification_result',
              timestamp: new Date().toISOString(),
              content: 'Classification complete',
              result: {
                hts_classification: result?.hts_classification,
                tariff_determination: result?.tariff_determination,
                classification_id: result?.classification_id,
                document_id: result?.document_id,
                extracted_data: result?.extracted_data,
              },
            });

            conversationService.updateConversation(
              currentConversationId,
              userId,
              messagesToSave
            ).catch((err: any) => {
              console.warn('[chatStore] Failed to persist result to conversation:', err);
            });
          }
        }
      },

      resumePolling: () => {
        const { currentJobId, setProcessingStatus, addMessage } = get();

        if (!currentJobId) return;

        documentService.watchJobSSE(
          currentJobId,
          {
            onProgress: (status) => {
              if (status === 'finalizing') setProcessingStatus(ProcessingStatus.FINALIZING);
            },
            onPartialResult: (htsClassification) => {
              // Guard: skip if any RESULT card already exists (loading or complete)
              const alreadyShown = get().messages.some(
                (m) => m.type === MessageType.RESULT
              );
              if (alreadyShown) return;
              addMessage({
                type: MessageType.RESULT,
                result: { hts_classification: htsClassification, tariff_loading: true },
              });
            },
          }
        )
          .then((result) => {
            setProcessingStatus(ProcessingStatus.COMPLETED);
            get()._applyFinalResult(result);
            setProcessingStatus(ProcessingStatus.IDLE);
          })
          .catch((error: any) => {
            console.error('Post-verification processing error:', error);
            setProcessingStatus(ProcessingStatus.FAILED);
            addMessage({
              type: MessageType.ERROR,
              content: `Error: ${error.message}`,
              error: error.message,
            });
            setProcessingStatus(ProcessingStatus.IDLE);
          });
      },

      // Conversation management methods
      setConversationId: (conversationId: string | null) => {
        console.log('[chatStore] Setting conversation_id:', conversationId);
        set({ currentConversationId: conversationId });
      },

      startNewConversation: () => {
        console.log('[chatStore] Starting new conversation');

        // Track new conversation
        analyticsService.trackNewConversation();

        set({
          messages: [],
          currentConversationId: null,
          currentJobId: null,
          processingStatus: ProcessingStatus.IDLE,
          uploadProgress: 0,
          error: null,
          verificationSessionId: null,
          verificationStatus: null,
          currentQuestion: null,
          verifiedData: null,
          isVerifying: false,
          pendingReclassification: null,
        });
      },

      loadConversation: async (conversationId: string, userId: string) => {
        const { setError, setProcessingStatus } = get();

        try {
          setProcessingStatus(ProcessingStatus.IDLE);
          setError(null);

          console.log('[chatStore] Loading conversation:', conversationId);

          // Fetch conversation from backend
          const conversation = await conversationService.getConversation(conversationId, userId);

          console.log('✅ [chatStore] Conversation loaded:', conversation.messages.length, 'messages');

          // Convert timestamp strings back to Date objects
          const messagesWithDates = conversation.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          })) as Message[];

          // Filter out transient messages that shouldn't appear in history
          const filteredMessages = messagesWithDates.filter((msg) => {
            if (msg.type === MessageType.THINKING) return false;
            if (msg.type === MessageType.PROCESSING) return false;
            // Strip reasoning messages with no actual steps (pre-persistence conversations)
            const steps = (msg as any).steps;
            if (msg.type === MessageType.REASONING && (!Array.isArray(steps) || steps.length === 0)) return false;
            // Strip transient "Classifying your product..." placeholder
            if (msg.type === MessageType.ASSISTANT && msg.content?.startsWith('Classifying your product')) return false;
            return true;
          });

          // Replace current messages with loaded conversation
          set({
            messages: filteredMessages,
            currentConversationId: conversation.conversation_id,
            currentJobId: null,
            processingStatus: ProcessingStatus.IDLE,
            uploadProgress: 0,
            error: null,
            // Reset verification state
            verificationSessionId: null,
            verificationStatus: null,
            currentQuestion: null,
            verifiedData: null,
            isVerifying: false,
            // Reset reclassification state
            pendingReclassification: null,
          });

          console.log('✅ [chatStore] Conversation restored successfully');

          // Track conversation opened
          analyticsService.trackConversationOpened(conversationId);
        } catch (error: any) {
          console.error('❌ [chatStore] Load conversation error:', error);
          setError(`Failed to load conversation: ${error.message}`);
        }
      },

      reset: () => {
        set({
          messages: [],
          currentJobId: null,
          currentConversationId: null,
          processingStatus: ProcessingStatus.IDLE,
          uploadProgress: 0,
          error: null,
          // Clear verification state
          verificationSessionId: null,
          verificationStatus: null,
          currentQuestion: null,
          verifiedData: null,
          isVerifying: false,
          // Clear feedback state
          feedbackMap: new Map(),
          // Clear reclassification state
          pendingReclassification: null,
          // Reset classification options
          skipTariff: true,
        });
      },

      // Feedback actions
      submitFeedback: async (classificationId: string, isPositive: boolean) => {
        console.log('[chatStore] submitFeedback called', { classificationId, isPositive });

        try {
          // Submit feedback to backend
          await feedbackService.submitFeedback(classificationId, isPositive);

          // Track feedback submission
          analyticsService.trackFeedback({
            jobId: classificationId,
            rating: isPositive ? 'positive' : 'negative',
          });

          // Update local feedback state
          set((state) => {
            const newFeedbackMap = new Map(state.feedbackMap);
            newFeedbackMap.set(classificationId, isPositive ? 'up' : 'down');
            return { feedbackMap: newFeedbackMap };
          });

          console.log('✅ [chatStore] Feedback submitted successfully');
        } catch (error: any) {
          console.error('❌ [chatStore] Submit feedback error:', error);
          throw error;
        }
      },

      // Reclassification actions
      requestReclassification: async (documentId: string, userMessage: string, file?: File) => {
        const { addMessage, setProcessingStatus, setError, currentConversationId } = get();

        console.log('[chatStore] requestReclassification called', { documentId, userMessage, hasFile: !!file, currentConversationId });

        try {
          setError(null);

          // Add user message to chat
          addMessage({
            type: MessageType.USER_TEXT,
            content: userMessage,
          });

          // Request reclassification — always pass conversation_id explicitly so past
          // conversations (older than 24h) are correctly linked and not missed by auto-discovery.
          console.log('🔄 [chatStore] Calling reclassificationService...');
          const response = await reclassificationService.requestReclassification(
            documentId,
            userMessage,
            'User feedback - thumbs down',
            true,
            currentConversationId || undefined
          );

          console.log('✅ [chatStore] Reclassification job created:', response.job_id);

          // Track reclassification started
          analyticsService.trackReclassificationRequested({
            documentId: documentId,
            jobId: response.job_id,
            userMessage: userMessage,
          });

          // Set current job ID
          set({ currentJobId: response.job_id });

          // Build processing message — include version info when available
          const versionInfo =
            response.previous_version != null && response.new_version != null
              ? ` (v${response.previous_version} → v${response.new_version})`
              : '';

          addMessage({
            type: MessageType.PROCESSING,
            content: response.message || `Reclassifying with your feedback${versionInfo}...`,
            jobId: response.job_id,
            status: response.status,
          });

          setProcessingStatus(ProcessingStatus.QUEUED);

          console.log('[chatStore] Starting job polling for:', response.job_id);

          // Poll for job status using existing SSE infrastructure
          await documentService.pollJobStatus(
            response.job_id,
            (statusUpdate: any) => {
              console.log('📡 [chatStore] Reclassification status update:', statusUpdate);
              get().updateLastMessage({
                status: statusUpdate.status,
                progress: statusUpdate.progress,
              } as Partial<Message>);

              if (statusUpdate.status === 'processing') {
                setProcessingStatus(ProcessingStatus.PROCESSING);
              }

              if (statusUpdate.status === 'finalizing') {
                setProcessingStatus(ProcessingStatus.FINALIZING);
              }
            }
          );

          console.log('✅ [chatStore] Reclassification complete');
          setProcessingStatus(ProcessingStatus.COMPLETED);

          // Reload the conversation from backend so the new result appended by the
          // reclassification job is shown in the thread (instead of a local-only message).
          if (currentConversationId) {
            const userId = useAuthStore.getState().getUserId();
            if (userId) {
              console.log('[chatStore] Reloading conversation after reclassification:', currentConversationId);
              await get().loadConversation(currentConversationId, userId);
            }
          }

          // Track reclassification completed
          analyticsService.trackReclassificationCompleted({
            documentId: documentId,
            jobId: response.job_id,
          });

          setProcessingStatus(ProcessingStatus.IDLE);
        } catch (error: any) {
          console.error('❌ [chatStore] Reclassification error:', error);

          setProcessingStatus(ProcessingStatus.FAILED);
          setError(error.message);

          addMessage({
            type: MessageType.ERROR,
            content: `Reclassification error: ${error.message}`,
            error: error.message,
          });

          // Track reclassification error
          analyticsService.trackReclassificationFailed({
            documentId: documentId,
            error: error.message,
          });

          setProcessingStatus(ProcessingStatus.IDLE);
        }
      },

      // Initiate reclassification flow
      initiateReclassification: (documentId: string, classificationId: string) => {
        const { addMessage } = get();

        console.log('[chatStore] initiateReclassification called', { documentId, classificationId });

        // Add system message asking for feedback
        addMessage({
          type: MessageType.SYSTEM,
          content: "What's missing or incorrect in this result? Your feedback helps us rerun and improve the classification.",
        });

        // Submit negative feedback
        get().submitFeedback(classificationId, false).catch(error => {
          console.error('Failed to submit thumbs down feedback:', error);
        });

        // Set pending reclassification state
        set({
          pendingReclassification: {
            documentId,
            classificationId,
          },
        });

        console.log('✅ [chatStore] Reclassification flow initiated - waiting for user input');
      },
    }),
    { name: 'ChatStore' }
  )
);

export default useChatStore;
